import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Info, Layers, RefreshCw } from "lucide-react";

import {
  ContextBar,
  ProductTypeSync,
  SyncResultCards,
} from "@/components/catalog";
import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  PageHeader,
} from "@/components/ui";
import { useCatalog } from "@/contexts/CatalogContext";
import { useTaxonomyResolutionTasksQuery } from "@/graphql/generated";
import { formatProductType } from "@/lib/formatProductType";
import { cn } from "@/lib/utils";

type MarketplaceSyncItem = ReturnType<
  typeof useCatalog
>["activeMarketplaces"][number];

const syncStatusConfig: Record<string, { dot: string; label: string }> = {
  SYNCED: { dot: "bg-emerald-500", label: "Synced" },
  SYNCING: {
    dot: "bg-indigo-500 animate-pulse",
    label: "Syncing…",
  },
  NOT_SYNCED: { dot: "bg-amber-400", label: "Not synced" },
  FAILED: { dot: "bg-rose-500", label: "Failed" },
};

export default function MarketplaceSync() {
  const { marketplaceCode: urlCode } = useParams<{
    marketplaceCode?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    isLoading: marketplacesLoading,
    activeMarketplaces,
    selectedMarketplace,
    selectMarketplace,
  } = useCatalog();

  const [showTooltip, setShowTooltip] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const productTypeParam = searchParams.get("productType")?.trim() ?? "";
  const productTypeLabelParam =
    searchParams.get("productTypeLabel")?.trim() ?? "";
  const canonicalCategoryIdParam =
    searchParams.get("canonicalCategoryId")?.trim() ?? "";
  const canonicalCategoryNameParam =
    searchParams.get("canonicalCategoryName")?.trim() ?? "";

  const initialProductType = useMemo(
    () =>
      productTypeParam
        ? {
            name: productTypeParam,
            displayName:
              productTypeLabelParam ||
              formatProductType(productTypeParam) ||
              productTypeParam,
            synced: true,
          }
        : null,
    [productTypeLabelParam, productTypeParam],
  );
  const initialCanonicalCategory = useMemo(
    () =>
      canonicalCategoryIdParam
        ? {
            categoryId: canonicalCategoryIdParam,
            path: canonicalCategoryIdParam,
            confidence: 1,
            reason: canonicalCategoryNameParam || "Current approved mapping",
          }
        : null,
    [canonicalCategoryIdParam, canonicalCategoryNameParam],
  );

  // Sync URL param → context selection.
  // When the URL has a code (e.g. /catalog/sync/shopify), select that marketplace.
  // When there's no code, redirect to the first active marketplace.
  useEffect(() => {
    if (marketplacesLoading || activeMarketplaces.length === 0) return;
    if (urlCode) {
      selectMarketplace(urlCode.toUpperCase());
    } else {
      const first = activeMarketplaces[0];
      if (first)
        navigate(`/catalog/sync/${first.code.toLowerCase()}`, {
          replace: true,
        });
    }
  }, [urlCode, marketplacesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeMarketplace = selectedMarketplace || activeMarketplaces[0];

  useEffect(() => {
    setSyncDone(false);
  }, [activeMarketplace?.id]);

  const { data: tasksData, isLoading: tasksLoading } =
    useTaxonomyResolutionTasksQuery(
      { marketplace: activeMarketplace?.code },
      { enabled: !!activeMarketplace?.code },
    );

  const syncResults = useMemo(() => {
    const tasks = tasksData?.taxonomyResolutionTasks ?? [];
    if (tasks.length === 0) return undefined;
    return {
      mappings: tasks.filter((t) => t.status === "RESOLVED").length,
      needsReview: tasks.filter((t) => t.status !== "RESOLVED").length,
    };
  }, [tasksData]);

  return (
    <DashboardLayout activePath="/catalog/sync">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <PageHeader
          title="Catalog Sync Center"
          subtitle="Sync marketplace catalogs against the SmartStore Canonical model"
          breadcrumbs={[
            { label: "Catalog", active: false },
            { label: "Catalog Sync", active: true },
          ]}
        />
        <div
          className="relative shrink-0 ml-4"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span
            data-testid="canonical-architecture-badge"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 cursor-help"
          >
            <Layers className="w-3.5 h-3.5" />
            Canonical-First Architecture
            <Info className="w-3 h-3 opacity-50" />
          </span>
          {showTooltip && (
            <div
              data-testid="canonical-tooltip"
              className="absolute right-0 top-full mt-2 z-50 w-72 p-3 rounded-lg text-xs text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg"
            >
              SmartStore maintains its own canonical catalog. All marketplaces
              sync against this model.
            </div>
          )}
        </div>
      </div>

      {/* ── Marketplace Strip ── */}
      <div
        data-testid="marketplace-selector-panel"
        className="mb-4 p-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
      >
        {marketplacesLoading ? (
          <div className="flex items-center gap-2 px-2 py-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-10 w-28 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
            {activeMarketplaces.map((mp: MarketplaceSyncItem) => {
              const isActive = activeMarketplace?.id === mp.id;
              const cfg =
                syncStatusConfig[mp.syncStatus] ?? syncStatusConfig.NOT_SYNCED;
              return (
                <button
                  key={mp.id}
                  data-testid={`marketplace-chip-${mp.id}`}
                  onClick={() =>
                    navigate(`/catalog/sync/${mp.code.toLowerCase()}`)
                  }
                  className={cn(
                    "flex items-center gap-2.5 pl-2.5 pr-3 py-2 rounded-lg border transition-all shrink-0 text-sm font-medium",
                    isActive
                      ? "bg-indigo-50 dark:bg-indigo-950/50 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-300/40 dark:ring-indigo-700/40"
                      : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-750 hover:border-slate-300 dark:hover:border-slate-600",
                  )}
                >
                  {mp.logo ? (
                    <img
                      src={mp.logo}
                      alt={mp.name}
                      className="w-5 h-5 object-contain shrink-0"
                    />
                  ) : (
                    <span className="w-5 h-5 rounded bg-slate-300 dark:bg-slate-600 shrink-0" />
                  )}
                  <span>{mp.name}</span>
                  <span
                    className={cn(
                      "w-2 h-2 rounded-full shrink-0 ml-0.5",
                      cfg.dot,
                    )}
                    title={cfg.label}
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <ContextBar />

      {/* ── Main Content (full-width) ── */}
      {activeMarketplace && (
        <div className="mt-4 space-y-6">
          {/* Sync Status Bar */}
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  {activeMarketplace.logo ? (
                    <img
                      src={activeMarketplace.logo}
                      alt={activeMarketplace.name}
                      className="w-6 h-6 object-contain"
                    />
                  ) : null}
                  {activeMarketplace.name}
                </CardTitle>

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                    <Layers className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="text-slate-900 dark:text-slate-100 font-medium">
                      SmartStore
                    </span>
                    <span className="text-slate-300 dark:text-slate-600 mx-1">
                      →
                    </span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {activeMarketplace.name}
                    </span>
                  </div>

                  <Button
                    data-testid="start-sync-btn"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      document
                        .getElementById("product-type-sync-section")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Sync Product Type
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Product Type Sync */}
          <div id="product-type-sync-section">
            <ProductTypeSync
              key={activeMarketplace.id}
              marketplaceCode={activeMarketplace.code}
              initialProductType={initialProductType}
              initialCanonicalCategory={initialCanonicalCategory}
              spApiMarketplaceId={
                (activeMarketplace.syncConfig?.spApiMarketplaceId as
                  | string
                  | undefined) ?? undefined
              }
              onSyncStatusChange={(_running, done) => setSyncDone(done)}
            />
          </div>

          {/* Sync Results Summary — revealed once a sync session completes */}
          {syncDone && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Sync Results Summary
              </h3>
              <SyncResultCards results={syncResults} isLoading={tasksLoading} />
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
